import { Button } from '@/components/ui/button';
import { getRoutes } from '@/routes/routePaths';
import { CheckCircle2, Gift, Info, Smartphone, UserCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { AccountPreview, ActivationState, PackageSummary } from '../index';

interface ActivationSuccessStepProps {
  packageSummary: PackageSummary;
  flow: ActivationState['flow'];
  accountPreview: AccountPreview | null;
  onReset: () => void;
}

export default function ActivationSuccessStep({
  packageSummary,
  flow,
  accountPreview,
  onReset,
}: ActivationSuccessStepProps) {
  const navigate = useNavigate();
  const isGift = flow === 'PAY_AND_GIFT';

  return (
    <div className='flex flex-col items-center gap-7 py-10 px-4 sm:px-0 text-center'>
      {/* Icon */}
      <div className='flex h-20 w-20 items-center justify-center rounded-full bg-[#1C2E1C]'>
        <CheckCircle2 className='h-10 w-10 text-[#4ADE80]' />
      </div>

      {/* Heading */}
      <div className='flex flex-col gap-2'>
        <h2 className='text-2xl font-bold text-white'>
          {isGift ? 'Gift Sent Successfully!' : 'You\'re All Set!'}
        </h2>
        <p className='text-sm text-[#A8ADB9] max-w-sm mx-auto leading-relaxed'>
          {isGift
            ? `Your payment was successful. The gift card has been sent to ${accountPreview?.displayName ?? 'the recipient'}.`
            : 'Your payment was successful. The sessions have been added to your FitStatic account.'}
        </p>
      </div>

      {/* Package summary card */}
      <div className='w-full max-w-sm rounded-xl border border-[#515764] p-5 bg-[#1C1F2E] text-left'>
        <p className='text-xs uppercase tracking-widest text-[#A8ADB9] mb-3'>
          {isGift ? 'Gift Package' : 'Activated Package'}
        </p>
        <div className='flex items-center justify-between'>
          <div>
            <p className='font-semibold text-white'>{packageSummary.planName}</p>
            <p className='text-sm text-[#A8ADB9] mt-0.5'>{packageSummary.programName}</p>
          </div>
          <div className='text-right'>
            <p className='text-xl font-bold text-[#E9CC7C]'>${packageSummary.amount}</p>
            <p className='text-xs text-[#A8ADB9] mt-0.5'>
              {packageSummary.sessionCount} session{packageSummary.sessionCount !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Contextual detail block */}
      {isGift ? (
        /* Gift flow — recipient info + what happens next */
        <div className='w-full max-w-sm flex flex-col gap-3'>
          {/* Recipient card */}
          {accountPreview && (
            <div className='flex items-center gap-3 rounded-xl border border-[#2A2D42] bg-[#1C1F2E] p-4 text-left'>
              {accountPreview.profileImage ? (
                <img
                  src={accountPreview.profileImage}
                  alt={accountPreview.displayName}
                  className='h-10 w-10 rounded-full object-cover shrink-0'
                />
              ) : (
                <UserCircle className='h-10 w-10 text-[#6B7280] shrink-0' />
              )}
              <div className='min-w-0'>
                <p className='text-xs text-[#A8ADB9]'>Gift sent to</p>
                <p className='font-semibold text-white text-sm truncate'>
                  {accountPreview.displayName}
                </p>
                {accountPreview.phoneMasked && (
                  <p className='text-xs text-[#A8ADB9] truncate'>{accountPreview.phoneMasked}</p>
                )}
              </div>
            </div>
          )}

          {/* What happens next */}
          <div className='rounded-xl border border-[#2A2D42] bg-[#1C1F2E] p-4 text-left flex flex-col gap-3'>
            <p className='text-xs font-semibold text-white uppercase tracking-wider'>
              What happens next
            </p>
            <div className='flex items-start gap-3'>
              <Gift className='h-4 w-4 text-[#F8B107] mt-0.5 shrink-0' />
              <p className='text-xs text-[#A8ADB9] leading-relaxed'>
                The recipient will receive an email with their gift card code and instructions on
                how to redeem it on the FitStatic app.
              </p>
            </div>
            <div className='flex items-start gap-3'>
              <Smartphone className='h-4 w-4 text-[#0A7AFF] mt-0.5 shrink-0' />
              <p className='text-xs text-[#A8ADB9] leading-relaxed'>
                Once they redeem their card in the app, the{' '}
                <span className='text-white font-medium'>
                  {packageSummary.sessionCount} session
                  {packageSummary.sessionCount !== 1 ? 's' : ''}
                </span>{' '}
                will be credited to their account instantly.
              </p>
            </div>
          </div>
        </div>
      ) : (
        /* Self flow — what was activated + next steps */
        <div className='w-full max-w-sm flex flex-col gap-3'>
          {/* Account credited */}
          {accountPreview && (
            <div className='flex items-center gap-3 rounded-xl border border-[#2A2D42] bg-[#1C1F2E] p-4 text-left'>
              {accountPreview.profileImage ? (
                <img
                  src={accountPreview.profileImage}
                  alt={accountPreview.displayName}
                  className='h-10 w-10 rounded-full object-cover shrink-0'
                />
              ) : (
                <UserCircle className='h-10 w-10 text-[#6B7280] shrink-0' />
              )}
              <div className='min-w-0'>
                <p className='text-xs text-[#A8ADB9]'>Sessions added to</p>
                <p className='font-semibold text-white text-sm truncate'>
                  {accountPreview.displayName}
                </p>
                {accountPreview.phoneMasked && (
                  <p className='text-xs text-[#A8ADB9] truncate'>{accountPreview.phoneMasked}</p>
                )}
              </div>
            </div>
          )}

          {/* What to do next */}
          <div className='rounded-xl border border-[#2A2D42] bg-[#1C1F2E] p-4 text-left flex flex-col gap-3'>
            <p className='text-xs font-semibold text-white uppercase tracking-wider'>
              What to do next
            </p>
            <div className='flex items-start gap-3'>
              <Smartphone className='h-4 w-4 text-[#0A7AFF] mt-0.5 shrink-0' />
              <p className='text-xs text-[#A8ADB9] leading-relaxed'>
                Open the FitStatic app and sign in to your account. Your{' '}
                <span className='text-white font-medium'>
                  {packageSummary.sessionCount} session
                  {packageSummary.sessionCount !== 1 ? 's' : ''}
                </span>{' '}
                are ready — start scheduling your {packageSummary.programName} sessions right away.
              </p>
            </div>
            <div className='flex items-start gap-3'>
              <Info className='h-4 w-4 text-[#F8B107] mt-0.5 shrink-0' />
              <p className='text-xs text-[#A8ADB9] leading-relaxed'>
                A payment confirmation has been sent to your email address for your records.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* CTAs */}
      <div className='flex flex-col gap-3 w-full max-w-sm'>
        <Button
          id='gift-card-login-btn'
          className='w-full h-12 font-bold text-black'
          style={{
            background: 'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
          }}
          onClick={() => navigate(getRoutes.Login)}
        >
          {isGift ? 'Back to FitStatic' : 'Sign In & Start Booking'}
        </Button>
        <Button
          id='activate-another-btn'
          variant='ghost'
          className='w-full h-12 text-[#A8ADB9] hover:text-white'
          onClick={onReset}
        >
          {isGift ? 'Send Another Gift Card' : 'Activate Another Gift Card'}
        </Button>
      </div>
    </div>
  );
}
